import { useState, useEffect, useCallback } from 'react';
import api from '../config/api';
import { TERMS_VERSION } from '../config/terms';

export const useTermsAcceptance = () => {
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(true); // Default to true to not block UI initially
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Check if user has already accepted terms
  const checkTermsAcceptance = useCallback(async () => {
    const token = localStorage.getItem('token');
    
    // Not logged in - don't show modal
    if (!token) {
      setHasAcceptedTerms(true);
      setShowTermsModal(false);
      setIsLoading(false);
      return;
    }

    try {
      const response = await api.get('/auth/terms-status');
      const { hasAcceptedTerms: accepted } = response.data;
      
      setHasAcceptedTerms(accepted);
      setShowTermsModal(!accepted);
    } catch (error) {
      console.error('Error checking terms acceptance:', error);
      // On error, check localStorage as fallback
      const stored = localStorage.getItem('terms_accepted');
      if (stored) {
        try {
          const termsData = JSON.parse(stored);
          setHasAcceptedTerms(!!termsData.accepted);
          setShowTermsModal(!termsData.accepted);
        } catch {
          setHasAcceptedTerms(false);
          setShowTermsModal(true);
        }
      } else {
        // No acceptance found - show modal
        setHasAcceptedTerms(false);
        setShowTermsModal(true);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    checkTermsAcceptance();
  }, [checkTermsAcceptance]);

  const handleAcceptTerms = useCallback(async () => {
    try {
      // Save to backend
      await api.post('/auth/accept-terms');
      
      // Also save to localStorage as backup
      localStorage.setItem('terms_accepted', JSON.stringify({
        accepted: true,
        timestamp: Date.now(),
        version: TERMS_VERSION
      }));
      
      setHasAcceptedTerms(true);
      setShowTermsModal(false);
    } catch (error) {
      console.error('Error saving terms acceptance:', error);
      // Still update UI even if backend fails
      localStorage.setItem('terms_accepted', JSON.stringify({
        accepted: true,
        timestamp: Date.now(),
        version: TERMS_VERSION
      }));
      setHasAcceptedTerms(true);
      setShowTermsModal(false);
    }
  }, []);

  const handleShowTermsModal = useCallback(() => {
    setShowTermsModal(true);
  }, []);

  const handleCloseTermsModal = useCallback(() => {
    // Only allow closing if terms are accepted
    if (hasAcceptedTerms) {
      setShowTermsModal(false);
    }
  }, [hasAcceptedTerms]);

  return {
    hasAcceptedTerms,
    showTermsModal,
    isLoading,
    handleAcceptTerms,
    handleShowTermsModal,
    handleCloseTermsModal
  };
};
